import React from "react";
import { Box, Typography } from "@mui/material";
import HPDropdown from "@/components/common/Dropdown";
import HPTag, { HPTagColor } from "@/components/common/Tag";
import type { ScorecardBrandCandidate } from "@/models/Scorecard";

interface BrandSelectorProps {
  brandCandidates: ScorecardBrandCandidate[];
  primaryBrandSourceId: string | null;
  competitorBrandSourceIds: string[];
  onPrimaryChange: (sourceId: string) => void;
  onCompetitorsChange: (sourceIds: string[]) => void;
}

const BrandSelector: React.FC<BrandSelectorProps> = ({
  brandCandidates,
  primaryBrandSourceId,
  competitorBrandSourceIds,
  onPrimaryChange,
  onCompetitorsChange,
}) => {
  const primaryOptions = brandCandidates.map((c) => ({
    value: c.sourceId,
    label: c.label,
  }));
  const competitorOptions = brandCandidates
    .filter((c) => c.sourceId !== primaryBrandSourceId)
    .map((c) => ({ value: c.sourceId, label: c.label }));

  // Map competitor IDs to labels for display
  const getCompetitorLabel = (sourceId: string): string => {
    return brandCandidates.find((c) => c.sourceId === sourceId)?.label || sourceId;
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
        Brands*
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Select exactly one Primary Brand and at least one Competitor Brand.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <HPDropdown
          label="Primary Brand*"
          value={primaryBrandSourceId ?? ""}
          onChange={onPrimaryChange}
          options={primaryOptions}
          placeholder="Select Primary Brand"
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <HPDropdown
            label="Competitor Brands*"
            value={competitorBrandSourceIds}
            onChange={onCompetitorsChange}
            options={competitorOptions}
            multiSelect
            countLabel="Competitor"
            placeholder="Select Competitor Brands"
          />

          {/* Read-only competitor tags display */}
          {competitorBrandSourceIds.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {competitorBrandSourceIds.map((competitorId) => (
                <HPTag
                  key={competitorId}
                  ariaLabel={getCompetitorLabel(competitorId)}
                  color={HPTagColor.BLUE}
                  hasBorder={true}
                >
                  {getCompetitorLabel(competitorId)}
                </HPTag>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BrandSelector;
